import { CloseOutlined } from '@ant-design/icons';
import { DatePicker, Input, Modal, Typography } from 'antd';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';

import {
  DATE_FORMATS,
  DATE_PICKER_FORMAT,
  MAX_FIELD_LENGTH_5000,
  PREFIX,
  rangePickerLocale,
} from '@app/constants';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { divideAmount } from '@app/utils';

import type { PrepaymentModalProps } from '../types';
import type { Dayjs } from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;

export const PrepaymentModal: React.FC<PrepaymentModalProps> = ({
  isPrepaymentDialogOpen,
  paymentAmount,
  onOk,
  onCancel,
  recordDate,
  recordId,
  receiptId,
}) => {
  const { showNotification } = useContext(NotificationContext);

  const [date, setDate] = useState<string>('');
  const [comment, setComment] = useState<string>('');

  const [isConfirmationDisabling, setIsConfirmationDisabling] = useState(false);

  useEffect(() => {
    if (recordDate) {
      setDate(
        dayjs(recordDate)
          .startOf('day')
          .format(DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE)
      );
    }
  }, [recordDate]);

  const handleCancel = () => {
    onCancel();
    setComment('');
  };

  const handleOk = async () => {
    try {
      setIsConfirmationDisabling(true);

      await onOk({
        recordId,
        accountId: Number(receiptId),
        amount: Number(paymentAmount),
        paymentDate: date,
        comment,
      });

      handleCancel();
    } catch (error) {
      showNotification(handleCatchError(error));
    } finally {
      setIsConfirmationDisabling(false);
    }
  };

  return (
    <Modal
      data-testid="prepayment-popup"
      okText={<span data-testid="confirm">Подтвердить</span>}
      cancelText={<span data-testid="cancel">Отменить</span>}
      title={<span data-testid="prepayment-popup-title">Предоплата</span>}
      className={`${PREFIX}prepayment-modal`}
      zIndex={1001}
      open={isPrepaymentDialogOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      okButtonProps={{
        disabled: isConfirmationDisabling,
        loading: isConfirmationDisabling,
      }}
      cancelButtonProps={{ disabled: isConfirmationDisabling }}
      closeIcon={<CloseOutlined data-testid="close-popup" />}
    >
      <Text data-testid="prepayment-description">
        {`Внести предоплату на сумму ${divideAmount(paymentAmount)}?`}
      </Text>
      <div className="date-picker">
        <h4 data-testid="payment-date">Дата оплаты</h4>
        <DatePicker
          data-testid="date-for-payment-value"
          locale={rangePickerLocale}
          inputReadOnly={true}
          format={DATE_PICKER_FORMAT}
          size="large"
          allowClear={false}
          onChange={(date: Dayjs) => {
            if (date) {
              setDate(date.format(DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE));
            }
          }}
          defaultValue={dayjs(date)}
          value={dayjs(date)}
          classNames={{ popup: { root: 'calendar-block' } }}
        />
        <h4 data-testid="payment-date">Комментарий</h4>
        <TextArea
          value={comment}
          rows={4}
          maxLength={MAX_FIELD_LENGTH_5000}
          onChange={(event) => {
            setComment(event.target.value);
          }}
        />
      </div>
    </Modal>
  );
};
